'use client'

import { useRef, useState } from 'react'
import { useRouter } from '@bprogress/next/app'
import { useTranslations } from 'next-intl'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useCreateI18nMessage } from '@/hooks/use-i18n-messages'
import { I18nMessageForm } from '@/components/partials/i18n-messages'
import { type I18nMessageFormRef } from '@/components/partials/i18n-messages/form'
import { CreateI18nMessageParams } from '@/types/i18n-message'
import { FormModeEnum } from '@/enums'

const Create = () => {
  const t = useTranslations()
  const router = useRouter()
  const queryClient = useQueryClient()
  const messageFormRef = useRef<I18nMessageFormRef>(null)
  const [isRedirecting, setIsRedirecting] = useState(false)
  const [formKey, setFormKey] = useState(0)
  const { mutate: createI18nMessage, isPending: isLoading } = useCreateI18nMessage()

  const handleSubmit = (data: CreateI18nMessageParams) => {
    createI18nMessage(data, {
      onSuccess: async newMessage => {
        setIsRedirecting(true)
        queryClient.setQueryData(['i18nMessage', newMessage.id], newMessage)
        await queryClient.invalidateQueries({ queryKey: ['i18nMessages'] })
        toast.success(t('created_successfully'))
        router.push(`/localization/i18n-messages/${newMessage.id}`)
      },
      onError: handleError
    })
  }

  const handleSubmitAndNew = (data: CreateI18nMessageParams) => {
    createI18nMessage(data, {
      onSuccess: async newMessage => {
        queryClient.setQueryData(['i18nMessage', newMessage.id], newMessage)
        await queryClient.invalidateQueries({ queryKey: ['i18nMessages'] })
        const detailUrl = `/localization/i18n-messages/${newMessage.id}`
        toast.success(t('created_successfully'), {
          action: {
            label: t('view'),
            onClick: () => router.push(detailUrl)
          }
        })
        messageFormRef.current?.reset()
        setFormKey(prev => prev + 1)
      },
      onError: handleError
    })
  }

  const handleError = (error: any) => {
    if (error.response?.status === 422 || error.isValidationError) {
      if (messageFormRef.current) {
        messageFormRef.current.handleValidationErrors(error)
      }
      return
    }

    toast.error(t('error_creating'))
  }

  const handleCancel = () => {
    router.push('/localization/i18n-messages')
  }

  return (
    <I18nMessageForm
      key={formKey}
      ref={messageFormRef}
      mode={FormModeEnum.CREATE}
      onSubmit={handleSubmit}
      onSubmitAndNew={handleSubmitAndNew}
      onCancel={handleCancel}
      isLoading={isLoading || isRedirecting}
    />
  )
}

export default Create
